/**
 * useUpload Hook
 *
 * Handles S3 file uploads via presigned URLs.
 */
import { useState, useCallback } from 'react';
import { api } from '../services/api';

interface UploadState {
  isUploading: boolean;
  progress: number;
  error: string | null;
}

interface UseUploadReturn extends UploadState {
  upload: (file: File) => Promise<string | null>;
  reset: () => void;
}

export const useUpload = (): UseUploadReturn => {
  const [state, setState] = useState<UploadState>({
    isUploading: false,
    progress: 0,
    error: null,
  });

  const reset = useCallback(() => {
    setState({
      isUploading: false,
      progress: 0,
      error: null,
    });
  }, []);

  const upload = useCallback(async (file: File): Promise<string | null> => {
    setState({ isUploading: true, progress: 0, error: null });

    try {
      // Get presigned URL from backend
      const urlResponse = await api.admin.getUploadUrl(file.name, file.type);

      if (urlResponse.error || !urlResponse.data) {
        throw new Error(urlResponse.error || 'Failed to get upload URL');
      }

      const { uploadUrl, fileUrl } = urlResponse.data;

      setState((prev) => ({ ...prev, progress: 30 }));

      // Upload file directly to S3
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
        },
      });

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload file to S3');
      }

      setState({ isUploading: false, progress: 100, error: null });

      return fileUrl;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Upload failed';
      setState({ isUploading: false, progress: 0, error: errorMessage });
      return null;
    }
  }, []);

  return {
    ...state,
    upload,
    reset,
  };
};

export default useUpload;
